import React from 'react';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip,
} from 'recharts';
import { HiSun } from 'react-icons/hi';
import { PiSunHorizonFill } from 'react-icons/pi';

interface AreaGraphProps {
  sunriseTime: string;
  sunsetTime: string;
  current_time: string;
}

const lineData = Array.from({ length: 21 }, (_, i) => {
  const x = i / 20;
  const curved = Math.pow(Math.sin(x * Math.PI), 1.5);
  return {
    value: Math.round(curved * 100),
  };
});

function toSeconds(timeStr: string): number {
  if (!timeStr || !timeStr.includes(':')) {
    console.error('Invalid time string:', timeStr);
    return 0;
  }
  const [h, m, s] = timeStr.split(':').map(Number);
  return h * 3600 + m * 60 + (s || 0);
}

function toAMPM(timeStr: string): string {
  const [h, m] = timeStr.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

function getSunPositionIndex(time: string): number {
  const timeSec = toSeconds(time);
  const totalDaySeconds = 24 * 3600;
  const percent = timeSec / totalDaySeconds;
  return Math.round(percent * (lineData.length - 1));
}

const SunDot = ({ cx, cy }: { cx?: number; cy?: number }) => {
  return (
    <circle
      cx={cx}
      cy={cy}
      r={5}
      fill="#64748b"
      stroke="#cbd5e1"
      strokeWidth={2}
      style={{
        filter: 'drop-shadow(0 0 6px rgba(0,0,0,0.3))',
      }}
    />
  );
};

const AreaGraph: React.FC<AreaGraphProps> = ({ sunriseTime, sunsetTime, current_time }) => {
  const sunIndex = getSunPositionIndex(current_time);

  return (
    <div className="w-full max-w-[230px] aspect-[2/1] rounded-2xl bg-[#f9fbfd] shadow-sm relative ">
      {/* Sunset Label */}
      <div className="text-slate-400 text-[10px] flex items-center gap-1">
        <PiSunHorizonFill /> Sunset: {toAMPM(sunsetTime)}
      </div>

      {/* Graph */}
      <div className="relative w-full h-[50px] overflow-hidden rounded-md">
        {/* Shaded bottom half */}
        <div className="absolute inset-0 top-[60%] bg-slate-100 opacity-70 z-10 pointer-events-none" />

        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={lineData}>
            <Line
              type="monotone"
              dataKey="value"
              stroke="#94a3b8"
              strokeWidth={2}
              activeDot={false}
              isAnimationActive={false}
              dot={(props: any) => {
                const { index, key, ...rest } = props;
                if (index === sunIndex) {
                  return <SunDot key={`sun-dot-${index}`} {...rest} />;
                }
                return <circle key={`empty-dot-${index}`} cx={props.cx} cy={props.cy} r={0} />;
              }}
            />
            <ReferenceLine
              y={40}
              stroke="#CBD5E1"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
            <Tooltip
              content={({ active }) => {
                if (active) {
                  const [h, m] = current_time.split(':').map(Number);
                  const period = h >= 12 ? 'PM' : 'AM';
                  const hour12 = h % 12 || 12;
                  const formattedTime = `${hour12}:${String(m).padStart(2, '0')} ${period}`;
                  return (
                    <div className="bg-white p-1 border rounded shadow text-[15px] text-gray-600 whitespace-nowrap">
                      {formattedTime}
                    </div>
                  );
                }
                return null;
              }}
              cursor={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Sunrise Label */}
      <div className="text-slate-400 text-[10px] flex items-center gap-1">
        <HiSun /> Sunrise: {toAMPM(sunriseTime)}
      </div>
    </div>
  );
};

export default AreaGraph;
